import type { PropsWithChildren } from 'react';
import React from 'react';

import type { TypographyProps } from '@packages/fenrir/components';
import { SkeletonInferred } from '@packages/fenrir/components';
import { Stack, Typography } from '@packages/fenrir/components';
import { EMPTY_FIELD_FALLBACK } from 'ae-constants/constants';

export type DefinitionProps = PropsWithChildren<{
  term: string;
  isLoading?: boolean;
  skeletonProps?: React.ComponentProps<typeof SkeletonInferred>;
  fallback?: React.ReactNode;
}> &
  Omit<TypographyProps, 'children'>;

type DefinitionContentProps = Omit<
  DefinitionProps,
  'term' | 'spacing' | 'isLoading' | 'skeletonProps'
>;

const DefinitionContent: React.FC<DefinitionContentProps> = ({
  variant,
  children,
  fallback,
  ...rest
}) => {
  if (!children && typeof children !== 'number') {
    return (
      fallback ?? (
        <Typography variant={variant} color="textSecondary" {...rest}>
          {EMPTY_FIELD_FALLBACK}
        </Typography>
      )
    );
  }

  if (typeof children === 'object') {
    return children;
  }

  return (
    <Typography variant={variant} {...rest}>
      {children}
    </Typography>
  );
};

export const Definition: React.FC<DefinitionProps> = ({
  term,
  isLoading,
  skeletonProps,
  fallback,
  children,
  ...rest
}) => {
  return (
    <Stack spacing={1} sx={{ alignItems: 'flex-start', maxWidth: 1 }}>
      <Typography variant="metaLabel">{term}</Typography>
      <SkeletonInferred
        isLoading={isLoading}
        sx={{ width: 1 }}
        {...skeletonProps}
      >
        <DefinitionContent fallback={fallback} {...rest}>
          {children}
        </DefinitionContent>
      </SkeletonInferred>
    </Stack>
  );
};
